export const brand = {
  appName: 'Radsport Events',
  shortName: 'RE',
} as const;

// Brand berry scale (centered on #922C57 at the 600 level). 800 = Berry dark #6E2247.
export const brandColors = {
  50:  '#fbf2f6',
  100: '#f6e4ec',
  200: '#eac4d4',
  300: '#da9bb5',
  400: '#c4708f',
  500: '#a94873',
  600: '#922C57',
  700: '#7c264a',
  800: '#6E2247',
  900: '#4f1833',
  950: '#2e0d1d',
} as const;

/**
 * Semantic design tokens for the Rad Events redesign (Berry Red + Graphite rebrand).
 * Single source of truth shared by web (App.css / inline styles) and mobile (StyleSheet).
 */
export const tokens = {
  // Surfaces (warm neutral)
  pageBg:       '#F5F3F3',
  surface:      '#FFFFFF',
  surfaceAlt:   '#F5F3F3',
  border:       '#E5E1E1',
  borderStrong: '#C9C4C4',
  divider:      '#EDE9E9',
  // Ink / text (graphite + warm grays)
  ink:          '#2C2C2C',
  textPrimary:  '#2C2C2C',
  textBody:     '#4A4A4A',
  textSecondary:'#6A6A6A',
  textMuted:    '#9A9494',
  textFaint:    '#C4BFBF',
  // Brand + accents (berry)
  primary:      '#922C57',
  primarySoft:  'rgba(146,44,87,0.08)',
  primarySoft12:'rgba(146,44,87,0.12)',
  accentPink:   '#C25078',
  accentPinkDeep:'#A82F5A',
  berryRoseLight:'#D08BA3',
  berryDark:    '#6E2247',
  destructive:  '#A82F5A',
  success:      '#1F8A5B',
  successSoft:  'rgba(31,138,91,0.10)',
  warning:      '#E0A100',
  // Gradients (profile header / login / hero cards)
  profileGradient: 'linear-gradient(135deg,#3A2E34 0%,#6E2247 135%)',
  bannerGradient:  'linear-gradient(135deg,#2C2C2C 0%,#6E2247 100%)',
} as const;

/** Discipline/category accent colors (keyed by EventCategory). */
export const CATEGORY_COLORS: Record<string, string> = {
  ROAD:   '#D23A2B',
  GRAVEL: '#9A5B2E',
  MTB:    '#7E8A3A',
};

/**
 * Aufgehellte Zwillinge der Disziplin-Farben für die invertierte Card (Hervorhebung 1c).
 * Die hellen Werte oben sind für weißen Grund gemacht und fallen auf `PROMO.invertedBg`
 * zusammen — hier derselbe Buntton, auf ~62 % Helligkeit gebracht. Jeder Wert erreicht ≥4.5:1
 * gegen `#241A1F`; wer eine neue Kategorie ergänzt, muss das nachrechnen.
 */
export const CATEGORY_COLORS_DARK: Record<string, string> = {
  ROAD:   '#DE695D',
  GRAVEL: '#D19A6A',
  MTB:    '#B9C576',
};

/**
 * Farbtoken der bezahlten Card-Hervorhebung (Design-Handoff „Event-Card Hervorhebung").
 * `frame*` = Stufe 1a Signatur-Rahmen, `inverted*` = Stufe 1c invertierte Card.
 *
 * Die halbtransparenten Werte bleiben bewusst transparent statt ausgerechnet: sie stimmen dann
 * auch, wenn der dunkle Grund einmal einen anderen Ton bekommt.
 */
export const PROMO = {
  frameBg:      '#FFF9FB',
  frameBorder:  '#922C57',
  frameHead:    'linear-gradient(120deg,#922C57 0%,#5E1B39 100%)',
  frameShadow:  '0 10px 30px rgba(146,44,87,0.18)',
  frameChipBorder: '#E9DCE3',

  invertedBg:     '#241A1F',
  invertedHead:   'linear-gradient(120deg,#3A2029 0%,#241A1F 100%)',
  invertedShadow: '0 14px 34px rgba(20,14,18,0.22)',
  invertedAccent: '#F0C36A',
  invertedTitle:  '#FFFFFF',
  invertedText:   '#B6ABA5',
  invertedTextDim:'#7E736E',
  invertedChipText: '#F4EEEA',
  invertedSurface:  'rgba(255,255,255,0.08)',
  invertedBorder:   'rgba(255,255,255,0.14)',
  invertedSuccess:  '#8CE0B6',
  invertedSuccessBg:'rgba(140,224,182,0.14)',

  /* Stufe 1b „Bühne": weiß wie eine normale Card — ihre Wirkung kommt aus Fläche und Foto, nicht
     aus Farbe. Genau deshalb verträgt sie sich mit 1a und 1c auf derselben Seite. */
  stageShadow:   '0 18px 42px rgba(20,14,18,0.14)',
  stagePhotoW:   250,
  stageWeekday:  '#8A857F',
  /* Nur auf der hochkant-Variante (2a): das Foto trägt oben das Label und unten das Datum. */
  stageScrim:    'linear-gradient(180deg, rgba(20,14,18,0.55) 0%, rgba(20,14,18,0) 45%, rgba(20,14,18,0.55) 100%)',
  stageTextShadow: '0 1px 4px rgba(0,0,0,0.5)',
  stageIconShadow: '0 2px 8px rgba(0,0,0,0.25)',
} as const;

/**
 * Voreinstellung der Pflicht-Kennzeichnung je Stufe. Überschreibbar, aber nie leer — ohne
 * Kennzeichnung darf keine bezahlte Card ausgeliefert werden.
 *
 * Beide Stufen tragen bewusst denselben Text. Der Entwurf sah für die helle Stufe „TOP-EVENT" vor;
 * das liest sich wie eine redaktionelle Bewertung und ist damit genau der Fall, den das
 * Trennungsgebot trifft (getarnte Werbung, Anhang zu § 3 Abs. 3 UWG Nr. 11). Rechtlich sind beide
 * Stufen dasselbe — bezahlte Platzierung —, also kennzeichnen sie auch gleich. Das
 * Verkaufsargument trägt ohnehin die Optik, nicht das Wort.
 */
export const PROMOTION_DEFAULT_LABELS: Record<string, string> = {
  FRAME: 'ANZEIGE',
  INVERTED: 'ANZEIGE',
  // Der Entwurf schlug für die Bühne „PRÄSENTIERT" vor. Auch das umschreibt die Bezahlung, statt
  // sie zu benennen — und je größer die Fläche, desto weniger darf offen bleiben, dass sie
  // gekauft ist.
  STAGE: 'ANZEIGE',
};

/**
 * Die Stufen in aufsteigender Wirkung — und damit in aufsteigendem Preis. Reihenfolge jeder
 * Auswahl (Buchungsdialog, Preispflege, Admin-Dialog), damit sie überall dieselbe ist.
 */
export const PROMOTION_TIERS = ['FRAME', 'INVERTED', 'STAGE'] as const;

/** Anzeigename der Stufe im Admin-Bereich. */
export const PROMOTION_TIER_LABELS: Record<string, string> = {
  FRAME: 'Signatur-Rahmen (hell)',
  INVERTED: 'Invertierte Card (dunkel)',
  STAGE: 'Bühne (doppelte Breite, mit Foto)',
};

/**
 * Was die Stufe dem Veranstalter bringt — in seiner Sprache, für den Buchungsdialog. Der
 * Admin-Bereich nennt die Bauform (`PROMOTION_TIER_LABELS`), der Kunde die Wirkung.
 */
export const PROMOTION_TIER_SALES: Record<string, { name: string; benefit: string }> = {
  FRAME: {
    name: 'Signatur-Rahmen',
    benefit: 'Deine Card bekommt einen farbigen Rahmen und sticht aus der Liste heraus.',
  },
  INVERTED: {
    name: 'Dunkle Card',
    benefit: 'Deine Card wird dunkel, alle anderen bleiben hell. Man sieht sie zuerst.',
  },
  STAGE: {
    name: 'Bühne',
    benefit: 'Doppelte Breite mit deinem Foto und einem Knopf direkt zur Anmeldung. Braucht ein Bild am Event.',
  },
};

/**
 * Das Verkaufsmodell der Hervorhebung seit dem 11.09.2026 (`docs/monetarisierung.md`): **eine**
 * Stufe zum Start, der Rahmen, immer bis zur Veranstaltung. Die dunkle Card ist kein zweiter
 * Preis daneben, sondern ein Häkchen im Checkout mit dem Aufpreis als Zahl. Die Bühne bleibt
 * vorerst draußen (in `price_items` abgeschaltet). Keine Stufe setzt mehr Premium voraus, seit
 * Bild und Flyer am Event kostenlos sind — die Bühne braucht nur noch ein Foto.
 */
export const PROMOTION_BASE_TIER = 'FRAME';
export const PROMOTION_UPSELL_TIER = 'INVERTED';

/**
 * Welches Symbol neben einem Premium-Vorteil steht.
 *
 * Ein Name statt eines fertigen SVG, weil Web und App verschiedene Zeichenwege haben (`<svg>`
 * gegen `react-native-svg`). Die Liste bleibt damit plattformfrei — sonst müsste sie doppelt
 * gepflegt werden, und genau das soll sie nicht.
 */
export type PremiumIcon = 'megaphone' | 'chart' | 'image' | 'star';

/**
 * Was für jeden Veranstalter kostenlos ist, sobald er sein Profil selbst pflegt.
 *
 * Steht neben [PREMIUM_BENEFITS], weil ein Preisgespräch beide Hälften braucht: ohne die
 * kostenlose Seite liest sich die bezahlte wie eine Mautstelle vor der eigenen Vereinsseite.
 * Reihenfolge nach dem, was ein Verein zuerst will (13.09.2026): Events, dann Bild und Flyer,
 * dann Haken und Logo in einem Punkt — beides ist Identität, und getrennt gezählt las sich die
 * Liste gestreckt.
 */
export const ORGANIZER_FREE_BENEFITS: { title: string; text: string }[] = [
  {
    title: 'Events anlegen und pflegen',
    text: 'Beliebig viele Veranstaltungen, mit Strecken, Anmeldeterminen und Links, jederzeit änderbar.',
  },
  {
    title: 'Bild und Flyer am Event',
    text: 'Ein Foto und die Ausschreibung als PDF, direkt an jeder Veranstaltung.',
  },
  {
    title: 'Verifiziert-Haken und Logo',
    text: 'Der Haken sagt: das hier gehört wirklich dir. Dazu dein Vereinslogo auf deiner Seite und an deinen Events.',
  },
];

/**
 * Was Premium bringt — die eine Liste, die Web und App zeigen.
 *
 * Steht hier und nicht in zwei Oberflächen, weil sie ein Verkaufsversprechen ist: zwei Listen
 * driften auseinander, und dann verspricht die App etwas anderes als der Browser.
 *
 * **Vier Abschnitte, einer je Sache, die ein Verein bekommt** (26.08.2026). Vorher stand der
 * E-Mail-Vorteil als eigener, umrandeter Block über vier weiteren Punkten — also zwei Listen mit
 * zwei Bauformen für dieselbe Frage („was kriege ich?"). Bild und Banner sind zusammengezogen:
 * beides ist der eigene Auftritt, und getrennt gezählt sahen sie nach Streckung der Liste aus.
 *
 * **Der erste Abschnitt bleibt hervorgehoben** (im Dialog und in der App; die Verkaufskarte
 * zeichnet alle vier gleich, weil ein Kasten in einer Viererreihe die Reihe zerlegt). Er ist der
 * einzige Vorteil, den ein Verein nicht selbst herstellen kann: ein Foto legt er auf seine eigene
 * Website — an die Leute, die seinen Termin bei *uns* vorgemerkt haben, kommt er nur über uns.
 * **An wen** die Mails gehen, steht bewusst nicht mehr in diesem Satz (02.09.2026): der Halbsatz
 * „an alle, die euer Event vorgemerkt haben" machte die kürzeste Leistung zur längsten Zeile.
 * Er steht dafür unmittelbar darüber auf der Verkaufskarte, und zwar mit der echten Zahl
 * („31 Leute haben deine Events vorgemerkt. Ohne Premium hören sie nichts von dir") — dort ist
 * er ein Argument, hier war er eine Fußnote.
 *
 * **Vier Regeln für alle Premium-Texte:**
 *
 * 1. **Keine Produktbegriffe** — nicht „Merkungen", nicht „Bühne", nicht „Impressions".
 *    Die Sprache eines Vorstands, der die Plattform zweimal im Jahr benutzt. Deshalb heißt die
 *    Bühne hier „breite Card mit Foto": das ist, was er sieht, und er muss dafür kein Wort von
 *    uns lernen.
 * 2. **Zwei Wörter Titel, ein Satz darunter — und der Satz zählt auf, statt zu werben**
 *    (02.09.2026). Vorher standen dort ganze Werbezeilen („Damit aus Interesse eine Teilnahme
 *    wird") über drei- bis vierzeiligen Absätzen; vier davon nebeneinander sind eine Textwand,
 *    und gelesen wird davon der erste. Wer mehr wissen will, fragt.
 * 3. **Der Titel ist die Beschriftung, der Satz die Leistung.** Bei dieser Länge trägt der Titel
 *    kein Argument mehr, sondern nur noch den Namen der Sache („Echte Einblicke", „Mehr
 *    Präsentation");
 *    das Argument steht im Satz, und zwar so konkret wie möglich — „7 Tage vor dem Event und zum
 *    Anmeldestart" überzeugt, „damit aus Interesse eine Teilnahme wird" behauptet.
 *    Prüffrage für jeden neuen Satz: steht dort etwas, das ein Verein nachrechnen kann?
 * 4. **„Event", nicht „Termin".** Der Reiter heißt „Meine Events", die Tabelle heißt `events`,
 *    die Seite heißt Radsport Events — „Termin" war ein zweites Wort für dieselbe Sache. Gilt
 *    nicht für „Termin" als *Datum* (Anmeldetermin) und nicht für die öffentliche SEO-Sprache,
 *    wo nach „Termine" gesucht wird.
 *
 * **Der Haken steht hier nicht.** Er hängt seit dem 25.08.2026 an der Verknüpfung des Kontos und
 * ist kostenlos — eine Identitätsaussage lässt sich nicht verkaufen, ohne dass sie genau das
 * aufhört zu sein. Siehe [ORGANIZER_FREE_BENEFITS].
 *
 * `lead` markiert den hervorgehobenen ersten Abschnitt — Web und App zeichnen ihn verschieden,
 * aber beide sollen denselben zuerst und abgesetzt zeigen.
 */
export const PREMIUM_BENEFITS: { title: string; text: string; icon: PremiumIcon; lead?: true }[] = [
  {
    title: 'Automatische Erinnerungen',
    text: 'Wer dein Event vorgemerkt hat, bekommt 7 Tage vorher und zum Anmeldestart eine Mail.',
    icon: 'megaphone',
    lead: true,
  },
  {
    title: 'Echte Einblicke',
    text: 'Wie oft eure Events angesehen, vorgemerkt und weitergeklickt wurden.',
    icon: 'chart',
  },
  {
    title: 'Eigenes Titelbild',
    text: 'Ein Banner in voller Breite über eurer Veranstalterseite.',
    icon: 'image',
  },
];

/**
 * Die Steuerangabe, die an jedem Preis steht: „zzgl. 19 % MwSt."
 *
 * Seit V159 sind Preise **netto** hinterlegt, weil bestellt wird, wer vorsteuerabzugsberechtigt
 * ist. Ein Nettopreis ohne diese Angabe wäre eine falsche Zahl, also gehören beide zusammen und
 * stehen zusammen an einer Stelle — Web und App lesen denselben Satz, und der Steuersatz kommt
 * aus dem Preis, nicht aus dem Text.
 *
 * „MwSt." und nicht „USt.": das Wort, unter dem ein Vereinskassenwart die Zahl kennt.
 */
export const vatNote = (vatPercent: number): string => `zzgl. ${vatPercent} % MwSt.`;

/**
 * Für wen die Nettopreise gelten — der Satz unter dem Angebot.
 *
 * Nettopreise darf man nennen, wenn das Angebot sich an Unternehmer richtet; bei uns sind das
 * Vereine, Agenturen und Veranstaltungsfirmen. Damit das niemand raten muss, steht es auf der
 * öffentlichen Seite ausdrücklich dabei, statt im Kleingedruckten zu verschwinden — kurz, weil
 * ein ausformulierter Rechtssatz an dieser Stelle nur nach Kleingedrucktem klingt.
 */
export const PRICE_AUDIENCE_NOTE =
  'Alle Preise netto zzgl. MwSt. Das Angebot richtet sich an Vereine und Unternehmen.';

/**
 * Die Benachrichtigungen, die wir für ein Event verschicken — in der Sprache des Veranstalters.
 *
 * Bewusst **ohne** Premium-Marke an den Einträgen: welche Mail-Art Premium-Veranstaltern
 * vorbehalten ist, steht in `email_type_settings` und wird im Dashboard umgeschaltet. Eine
 * zweite Angabe hier wäre eine Voreinstellung, die nach der ersten Umstellung falsch ist — und
 * zwar an der Stelle, an der sie jemand liest.
 *
 * Die Aufteilung folgt der Frage, wem eine Mail zuerst dient: die werbenden Mails (Erinnerung,
 * Anmeldung) holen Leute an den Start und gehören dem Veranstalter; Änderung, Absage und die
 * Rundbriefe schützen den *Teilnehmer* — eine verschwiegene Absage wäre ein Schaden für Dritte.
 */
export const NOTIFICATION_KINDS: { type: string; title: string; text: string }[] = [
  {
    type: 'REGISTRATION_NOTICE',
    title: 'Anmeldung steht an',
    text: 'Wenn die Anmeldung öffnet, und fünf Tage, bevor der Preis steigt.',
  },
  {
    type: 'FAVORITE_REMINDER',
    title: '7 Tage vorher',
    text: 'Eine Erinnerung an alle, die dein Event vorgemerkt haben.',
  },
  {
    type: 'NEW_EVENTS',
    title: 'Neu in der Region',
    text: 'Dein neues Event im wöchentlichen Rundbrief an alle, die deine Region abonniert haben.',
  },
  {
    type: 'WEEKLY_DIGEST',
    title: 'Wochenüberblick',
    text: 'Dein Event in der Montags-Mail an alle, die deine Region abonniert haben.',
  },
  {
    type: 'FAVORITE_NEXT_EDITION',
    title: 'Nächste Ausgabe',
    text: 'Steht der Termin fürs nächste Jahr, sagen wir es allen, die die letzte Ausgabe vorgemerkt hatten.',
  },
  {
    type: 'FAVORITE_CHANGE',
    title: 'Änderung am Event',
    text: 'Datum, Ort oder Strecken geändert, ausgebucht — wer vorgemerkt hat, erfährt es.',
  },
  {
    type: 'FAVORITE_CANCELLATION',
    title: 'Absage',
    text: 'Fällt das Event aus, sagen wir es allen, die ihn vorgemerkt haben.',
  },
];

/**
 * Beschriftung der Mail-Arten in Auswertungen — dieselben Worte wie in [NOTIFICATION_KINDS].
 *
 * Oben die wiederkehrenden Benachrichtigungen, die ein Veranstalter in seinen Zahlen sieht,
 * darunter die Mails, die nur im Versand-Protokoll auftauchen (`/admin/email`, Geschäftszahlen).
 * Beide Gruppen stehen in derselben Liste, weil dieselbe Zelle mal die eine und mal die andere
 * Art trifft: zwei getrennte Listen hatten sich bereits auseinanderentwickelt.
 */
export const MAIL_TYPE_LABELS: Record<string, string> = {
  REGISTRATION_NOTICE: 'Anmeldung steht an',
  FAVORITE_REMINDER: 'Erinnerung, 7 Tage vorher',
  NEW_EVENTS: 'Neu in der Region',
  WEEKLY_DIGEST: 'Wochenüberblick',
  FAVORITE_NEXT_EDITION: 'Nächste Ausgabe',
  FAVORITE_CHANGE: 'Änderung am Event',
  FAVORITE_CANCELLATION: 'Absage',

  CONFIRMATION: 'E-Mail bestätigen',
  WELCOME: 'Willkommen',
  LOGIN_CODE: 'Anmeldecode',
  EDIT_REVIEW: 'Vorschlag geprüft',
  ORGANIZER_ACCESS: 'Veranstalter-Zugang',
  BOOKING: 'Buchungsanfrage',
  OUTREACH: 'Outreach',
  INQUIRY_REPLY: 'Antwort auf Anfrage',
  CHALLENGE_TEAM: 'Winterchallenge-Team',
  NEWSLETTER: 'Rundmail',
  SHOP_ORDER: 'Shop-Bestellung',
  TEST: 'Test-Mail',
};

/** Unbekannte Arten stehen mit ihrem Schlüssel da: eine leere Zelle verschwiege sie. */
export const mailTypeLabel = (type: string): string => MAIL_TYPE_LABELS[type] || type;

/**
 * Ersatzbild eines Veranstalters ohne Logo: Farbe und Kürzel aus dem Namen.
 *
 * Steht hier und nicht in jeder Oberfläche, weil das Ergebnis eine **Wiedererkennung** ist: derselbe
 * Verein muss auf der Event-Seite, in der Veranstalterliste, auf seinem Profil und in der App
 * dieselbe Kachel haben. Vorher lag die Rechnung in vier Dateien gleich und in einer anders — die
 * Event-Seite nahm die Kategoriefarbe und nur einen Buchstaben, sodass ein und derselbe Verein je
 * nach Seite rot mit „R" oder gold mit „RK" aussah.
 *
 * Die Farbe hängt am Namen und nicht an einer Id: so bekommt auch ein Freitext-Veranstalter ohne
 * Datensatz eine stabile Kachel.
 */
export const ORGANIZER_AVATAR_COLORS = ['#922C57', '#D23A2B', '#7E8A3A', '#9A5B2E', '#1F8A5B', '#E0A100'];

export const organizerAvatarColor = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return ORGANIZER_AVATAR_COLORS[h % ORGANIZER_AVATAR_COLORS.length];
};

/** „RV Adler Kirchhellen" → „RK", „Radteam" → „RA", leer → „?". */
export const organizerInitials = (name: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Pro-race stage-type accent colors (keyed by German stage-type label). */
export const STAGE_TYPE_COLORS: Record<string, string> = {
  'Flach':      '#1F8A5B',
  'Hügelig':    '#E0A100',
  'Bergetappe': '#D23A2B',
  'Zeitfahren': '#6B6577',
};

/** Font family names (loaded via Google Fonts on web, @expo-google-fonts on mobile). */
export const fonts = {
  display: 'Archivo',        // headings — weights 600/700/800/900
  sans:    'Hanken Grotesk', // body — weights 400/500/600/700
  mono:    'Space Mono',     // uppercase mono labels — 400/700
} as const;

export const grayColors = {
  50:  '#f9fafb',
  100: '#f3f4f6',
  200: '#e5e7eb',
  300: '#d1d5db',
  400: '#9ca3af',
  500: '#6b7280',
  600: '#4b5563',
  700: '#374151',
  800: '#1f2937',
  900: '#111827',
} as const;

// German Bundesländer (ue-spelling). Kept as the default list for backwards compatibility.
export const BUNDESLAENDER = [
  'Baden-Wuerttemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thueringen',
] as const;

// Austria's 9 Bundesländer (ue-spelling). Region names are disjoint from Germany's, so the
// country is a pure function of the region — mirrors backend BundeslandNormalizer.
export const BUNDESLAENDER_AT = [
  'Burgenland',
  'Kaernten',
  'Niederoesterreich',
  'Oberoesterreich',
  'Salzburg',
  'Steiermark',
  'Tirol',
  'Vorarlberg',
  'Wien',
] as const;

// Switzerland's 26 Kantone (ASCII + URL-safe slug; mirrors backend BundeslandNormalizer.CANONICAL_CH).
// Multi-word cantons use a hyphen (St-Gallen) so the value survives as a URL slug; umlauts fold for
// display only. German canton names are canonical; French/Italian forms are aliases.
export const BUNDESLAENDER_CH = [
  'Aargau',
  'Appenzell-Ausserrhoden',
  'Appenzell-Innerrhoden',
  'Basel-Landschaft',
  'Basel-Stadt',
  'Bern',
  'Freiburg',
  'Genf',
  'Glarus',
  'Graubuenden',
  'Jura',
  'Luzern',
  'Neuenburg',
  'Nidwalden',
  'Obwalden',
  'Schaffhausen',
  'Schwyz',
  'Solothurn',
  'St-Gallen',
  'Tessin',
  'Thurgau',
  'Uri',
  'Waadt',
  'Wallis',
  'Zug',
  'Zuerich',
] as const;

/**
 * Weitere Länder, in denen Events liegen dürfen (Italien, Belgien, …).
 *
 * Diese Länder haben bewusst *keine* eigene Regionsgliederung: der Regionswert ist der Landesname
 * selbst. Damit bleibt die Invariante des Modells erhalten — das Land ist eine reine Funktion der
 * Region —, und Filter, SEO-Seiten, Sitemap und alle Formulare arbeiten unverändert weiter, ohne
 * dass ein zweites Ortsfeld durch die ganze Anwendung gereicht werden muss. Sollte ein Land später
 * genug Events für Regionalseiten haben, kann es eine eigene Regionsliste bekommen.
 *
 * Schreibweise wie bei den Regionen: ASCII/URL-tauglich (Umlaute als ue/ae/oe, Leerzeichen als
 * Bindestrich), Umlaute nur in der Anzeige. Spiegel von CANONICAL_FOREIGN im Backend.
 *
 * `overseas` trennt die Liste nur für die Anzeige in zwei Gruppen (Europa / Weltweit) — fachlich
 * sind beide identisch. Ohne die Trennung stünden über 70 Länder in einer einzigen Auswahlgruppe.
 */
export const FOREIGN_COUNTRIES = [
  { code: 'AL', region: 'Albanien' },
  { code: 'AD', region: 'Andorra' },
  { code: 'BE', region: 'Belgien' },
  { code: 'BA', region: 'Bosnien-Herzegowina', display: 'Bosnien und Herzegowina' },
  { code: 'BG', region: 'Bulgarien' },
  { code: 'DK', region: 'Daenemark', display: 'Dänemark' },
  { code: 'EE', region: 'Estland' },
  { code: 'FI', region: 'Finnland' },
  { code: 'FR', region: 'Frankreich' },
  { code: 'GR', region: 'Griechenland' },
  { code: 'GB', region: 'Grossbritannien', display: 'Großbritannien' },
  { code: 'IE', region: 'Irland' },
  { code: 'IS', region: 'Island' },
  { code: 'IT', region: 'Italien' },
  { code: 'HR', region: 'Kroatien' },
  { code: 'LV', region: 'Lettland' },
  { code: 'LI', region: 'Liechtenstein' },
  { code: 'LT', region: 'Litauen' },
  { code: 'LU', region: 'Luxemburg' },
  { code: 'MT', region: 'Malta' },
  { code: 'MD', region: 'Moldau' },
  { code: 'MC', region: 'Monaco' },
  { code: 'ME', region: 'Montenegro' },
  { code: 'NL', region: 'Niederlande' },
  { code: 'MK', region: 'Nordmazedonien' },
  { code: 'NO', region: 'Norwegen' },
  { code: 'PL', region: 'Polen' },
  { code: 'PT', region: 'Portugal' },
  { code: 'RO', region: 'Rumaenien', display: 'Rumänien' },
  { code: 'SM', region: 'San-Marino', display: 'San Marino' },
  { code: 'SE', region: 'Schweden' },
  { code: 'RS', region: 'Serbien' },
  { code: 'SK', region: 'Slowakei' },
  { code: 'SI', region: 'Slowenien' },
  { code: 'ES', region: 'Spanien' },
  { code: 'CZ', region: 'Tschechien' },
  { code: 'TR', region: 'Tuerkei', display: 'Türkei' },
  { code: 'UA', region: 'Ukraine' },
  { code: 'HU', region: 'Ungarn' },
  { code: 'CY', region: 'Zypern' },

  // Außerhalb Europas. Ultra- und Bikepacking-Rennen finden längst weltweit statt (Atlas
  // Mountain Race in Marokko, Race Across America, Tour Divide, Silk Road Mountain Race) — ohne
  // Landeswert ließe sich so ein Event nicht einmal anlegen, weil die Region Pflicht ist.
  { code: 'EG', region: 'Aegypten', display: 'Ägypten', overseas: true },
  { code: 'AR', region: 'Argentinien', overseas: true },
  { code: 'AM', region: 'Armenien', overseas: true },
  { code: 'AU', region: 'Australien', overseas: true },
  { code: 'BR', region: 'Brasilien', overseas: true },
  { code: 'CL', region: 'Chile', overseas: true },
  { code: 'CR', region: 'Costa-Rica', display: 'Costa Rica', overseas: true },
  { code: 'GE', region: 'Georgien', overseas: true },
  { code: 'IN', region: 'Indien', overseas: true },
  { code: 'ID', region: 'Indonesien', overseas: true },
  { code: 'IL', region: 'Israel', overseas: true },
  { code: 'JP', region: 'Japan', overseas: true },
  { code: 'JO', region: 'Jordanien', overseas: true },
  { code: 'CA', region: 'Kanada', overseas: true },
  { code: 'KZ', region: 'Kasachstan', overseas: true },
  { code: 'KE', region: 'Kenia', overseas: true },
  { code: 'KG', region: 'Kirgisistan', overseas: true },
  { code: 'CO', region: 'Kolumbien', overseas: true },
  { code: 'MA', region: 'Marokko', overseas: true },
  { code: 'MX', region: 'Mexiko', overseas: true },
  { code: 'NA', region: 'Namibia', overseas: true },
  { code: 'NP', region: 'Nepal', overseas: true },
  { code: 'NZ', region: 'Neuseeland', overseas: true },
  { code: 'OM', region: 'Oman', overseas: true },
  { code: 'PE', region: 'Peru', overseas: true },
  { code: 'RW', region: 'Ruanda', overseas: true },
  { code: 'ZA', region: 'Suedafrika', display: 'Südafrika', overseas: true },
  { code: 'KR', region: 'Suedkorea', display: 'Südkorea', overseas: true },
  { code: 'TW', region: 'Taiwan', overseas: true },
  { code: 'TZ', region: 'Tansania', overseas: true },
  { code: 'TH', region: 'Thailand', overseas: true },
  { code: 'TN', region: 'Tunesien', overseas: true },
  { code: 'US', region: 'USA', overseas: true },
  { code: 'AE', region: 'Vereinigte-Arabische-Emirate', display: 'Vereinigte Arabische Emirate', overseas: true },
  { code: 'VN', region: 'Vietnam', overseas: true },
] as const satisfies readonly { code: string; region: string; display?: string; overseas?: true }[];

/** Anzeigename eines weiteren Landes — mit Umlauten/Leerzeichen, wo er vom Regionswert abweicht. */
const foreignDisplay = (c: typeof FOREIGN_COUNTRIES[number]): string =>
  'display' in c ? c.display : c.region;

/** Nur die Regionswerte der weiteren Länder — flach, in derselben Reihenfolge. */
export const FOREIGN_REGIONS: readonly string[] = FOREIGN_COUNTRIES.map(c => c.region);

const isOverseas = (c: typeof FOREIGN_COUNTRIES[number]): boolean => 'overseas' in c;

/** Weitere Länder in Europa — eigene Anzeigegruppe, s. REGION_GROUPS. */
export const FOREIGN_REGIONS_EUROPE: readonly string[] =
  FOREIGN_COUNTRIES.filter(c => !isOverseas(c)).map(c => c.region);

/** Länder außerhalb Europas — eigene Anzeigegruppe, s. REGION_GROUPS. */
export const FOREIGN_REGIONS_OVERSEAS: readonly string[] =
  FOREIGN_COUNTRIES.filter(isOverseas).map(c => c.region);

/** Die drei Kernländer mit eigener Regionsgliederung (Bundesländer bzw. Kantone). */
export type CoreCountryCode = 'DE' | 'AT' | 'CH';
export type ForeignCountryCode = typeof FOREIGN_COUNTRIES[number]['code'];
export type CountryCode = CoreCountryCode | ForeignCountryCode;

export const CORE_COUNTRIES: readonly CoreCountryCode[] = ['DE', 'AT', 'CH'];

const foreignLabels = FOREIGN_COUNTRIES.reduce((acc, c) => {
  acc[c.code] = foreignDisplay(c);
  return acc;
}, {} as Record<ForeignCountryCode, string>);

const foreignRegionsByCode = FOREIGN_COUNTRIES.reduce((acc, c) => {
  acc[c.code] = [c.region];
  return acc;
}, {} as Record<ForeignCountryCode, readonly string[]>);

export const COUNTRY_LABELS: Record<CountryCode, string> = {
  DE: 'Deutschland',
  AT: 'Österreich',
  CH: 'Schweiz',
  ...foreignLabels,
};

/** Regions grouped by country, for country-aware filter dropdowns. */
export const BUNDESLAENDER_BY_COUNTRY: Record<CountryCode, readonly string[]> = {
  DE: BUNDESLAENDER,
  AT: BUNDESLAENDER_AT,
  CH: BUNDESLAENDER_CH,
  ...foreignRegionsByCode,
};

/**
 * Gruppen für Regions-Dropdowns: die drei Kernländer mit ihren Regionen, danach die weiteren
 * Länder — in Europa und außerhalb getrennt. Ein eigener optgroup je Auslandsland (mit genau
 * einem Eintrag) würde die Liste unlesbar machen; eine einzige Sammelgruppe wäre inzwischen über
 * 70 Einträge lang und im Formular nicht mehr zu überblicken.
 */
export const REGION_GROUPS: readonly { label: string; regions: readonly string[] }[] = [
  { label: COUNTRY_LABELS.DE, regions: BUNDESLAENDER },
  { label: COUNTRY_LABELS.AT, regions: BUNDESLAENDER_AT },
  { label: COUNTRY_LABELS.CH, regions: BUNDESLAENDER_CH },
  { label: 'Weitere Länder in Europa', regions: FOREIGN_REGIONS_EUROPE },
  { label: 'Weltweit', regions: FOREIGN_REGIONS_OVERSEAS },
];

/** Alle wählbaren Regionswerte (DE/AT/CH-Regionen + die weiteren Länder), in Anzeigereihenfolge. */
export const ALL_REGIONS: readonly string[] =
  REGION_GROUPS.reduce<string[]>((acc, g) => acc.concat(g.regions), []);

/** Regionswerte eines Landes — leer, wenn der Code kein bekanntes Land ist. */
export function regionsOfCountry(code?: string | null): readonly string[] {
  if (!code) return [];
  return BUNDESLAENDER_BY_COUNTRY[code.toUpperCase() as CountryCode] ?? [];
}

/**
 * Stellenzahl der Postleitzahl je Land — nur dort, wo die PLZ eine reine Ziffernfolge ist.
 * Länder mit Buchstaben oder Bindestrich im Code (Niederlande, Polen, Portugal, Irland,
 * Großbritannien, …) fehlen bewusst: dort bietet die Umkreissuche keine PLZ-Eingabe an, sondern
 * läuft über Ortsname und Karte. Spiegel von postalCodeDigits im Backend.
 */
export const POSTAL_CODE_DIGITS: Partial<Record<CountryCode, number>> = {
  DE: 5, AT: 4, CH: 4,
  AL: 4, BE: 4, BG: 4, CY: 4, DK: 4, HU: 4, LI: 4, LU: 4, MD: 4, MK: 4, NO: 4, SI: 4,
  BA: 5, CZ: 5, EE: 5, ES: 5, FI: 5, FR: 5, GR: 5, HR: 5, IT: 5, LT: 5, ME: 5, MC: 5,
  RS: 5, SE: 5, SK: 5, SM: 5, TR: 5, UA: 5,
  IS: 3, RO: 6,
  // Außerhalb Europas. Nicht gelistet, weil dort keine reine Ziffernfolge fester Länge:
  // Argentinien (C1425DKE), Kanada (K1A 0B1), Japan/Brasilien (mit Bindestrich), Taiwan (3 oder 5)
  // sowie Namibia, Ruanda und die VAE, die gar keine Postleitzahlen führen.
  AM: 4, AU: 4, GE: 4, NZ: 4, TN: 4, ZA: 4,
  EG: 5, CR: 5, ID: 5, JO: 5, KE: 5, MA: 5, MX: 5, NP: 5, PE: 5, KR: 5, TZ: 5, TH: 5, US: 5,
  IN: 6, CO: 6, KZ: 6, KG: 6, VN: 6,
  CL: 7, IL: 7, OM: 3,
};

/** Stellenzahl der PLZ eines Landes, oder null wenn dort keine Ziffern-PLZ existiert. */
export function postalCodeDigits(country?: string | null): number | null {
  if (!country) return null;
  return POSTAL_CODE_DIGITS[country.toUpperCase() as CountryCode] ?? null;
}

/**
 * Länderkürzel, wie sie in Adressen vor der PLZ stehen ("CH-8280", "A-9544", "E-11111").
 * Bewusst nur die im Bestand vorkommenden — die Liste soll nicht raten, sondern erkennen.
 */
const POSTAL_CODE_PREFIX_COUNTRY: Record<string, CountryCode> = {
  D: 'DE', A: 'AT', CH: 'CH', I: 'IT', F: 'FR', E: 'ES', B: 'BE', L: 'LU', NL: 'NL', DK: 'DK',
};

/** PLZ ohne führendes Länderkürzel ("CH-9423" → "9423"). Spiegel von normalizePostalCode im Backend. */
export function stripPostalCodePrefix(postalCode?: string | null): string {
  return (postalCode ?? '').trim().replace(/^[A-Za-z]{1,3}-/, '').trim();
}

/**
 * Hinweis, wenn PLZ und (Bundes)Land nicht zusammenpassen — oder null, wenn alles stimmig ist.
 *
 * Aus der Region leitet sich das Land ab und damit, in welchem Land die Adresse gesucht wird.
 * Passt beides nicht zusammen, findet der Kartendienst den Ort nie: "Kreuzlingen" mit der Region
 * Baden-Württemberg wird in Deutschland gesucht, der Ort liegt aber im Thurgau. Das Event landet
 * dann ohne Kartenpunkt in der Liste, und beim Speichern merkt es niemand.
 *
 * Bewusst ein **Hinweis, keine Sperre**: Sonderfälle gibt es immer, und ein hartes Verbot würde
 * das Anlegen eines korrekten Events verhindern, nur weil die Prüfung ihn nicht kennt.
 * Rückgabe ist der fertige Anzeigetext (deutsch) — die Formulare zeigen ihn unverändert.
 */
export function postalCodeRegionWarning(
  postalCode?: string | null,
  bundesland?: string | null,
): string | null {
  const raw = (postalCode ?? '').trim();
  if (!raw || !bundesland) return null;

  const country = countryOfBundesland(bundesland);
  if (!country) return null; // Unbekannte Region — dann ist auch nichts zu vergleichen.

  const regionLand = COUNTRY_LABELS[country];

  // 1. Kürzel vor der PLZ, das einem anderen Land gehört: der deutlichste Widerspruch.
  const prefixMatch = /^([A-Za-z]{1,3})-/.exec(raw);
  if (prefixMatch) {
    const prefixCountry = POSTAL_CODE_PREFIX_COUNTRY[prefixMatch[1].toUpperCase()];
    if (prefixCountry && prefixCountry !== country) {
      return `Die PLZ „${raw}" deutet auf ${COUNTRY_LABELS[prefixCountry]} hin, als (Bundes)Land ist aber `
        + `${displayBundesland(bundesland)} (${regionLand}) gewählt. Ohne passendes (Bundes)Land wird die `
        + `Adresse im falschen Land gesucht und das Event erscheint nicht auf der Karte.`;
    }
    return `Die PLZ bitte ohne das Länderkürzel eintragen — also „${stripPostalCodePrefix(raw)}" statt „${raw}".`;
  }

  // 2. Stellenzahl passt nicht zum Land der Region. Nur für reine Ziffern-PLZ prüfbar;
  //    "1012 AB" (Niederlande) oder "SW1A 1AA" haben kein festes Ziffernmaß.
  const expected = postalCodeDigits(country);
  const cleaned = stripPostalCodePrefix(raw);
  if (expected != null && /^\d+$/.test(cleaned) && cleaned.length !== expected) {
    return `${regionLand} hat ${expected}-stellige Postleitzahlen, eingetragen ist „${cleaned}" `
      + `(${cleaned.length} Stellen). Bitte PLZ und (Bundes)Land prüfen — sonst wird die Adresse `
      + `im falschen Land gesucht und das Event erscheint nicht auf der Karte.`;
  }

  return null;
}

// ── Bundesland normalization ────────────────────────────────────────────────
// Internally we store the ue-spelling (ASCII, matches SEO slugs & filter values);
// umlauts are restored only for display.
const BUNDESLAND_DISPLAY: Record<string, string> = {
  'Baden-Wuerttemberg': 'Baden-Württemberg',
  'Thueringen': 'Thüringen',
  // Austria
  'Kaernten': 'Kärnten',
  'Niederoesterreich': 'Niederösterreich',
  'Oberoesterreich': 'Oberösterreich',
  // Switzerland (only cantons whose display differs from the ASCII/URL-safe slug)
  'Graubuenden': 'Graubünden',
  'Zuerich': 'Zürich',
  'St-Gallen': 'St. Gallen',
  'Appenzell-Ausserrhoden': 'Appenzell Ausserrhoden',
  'Appenzell-Innerrhoden': 'Appenzell Innerrhoden',
};

// Weitere Länder (Regionswert = Landesname), soweit die Anzeige davon abweicht.
FOREIGN_COUNTRIES.forEach(c => {
  if (foreignDisplay(c) !== c.region) BUNDESLAND_DISPLAY[c.region] = foreignDisplay(c);
});

const COUNTRY_BY_KEY: Record<string, CountryCode> = {};

/** Display label for a Bundesland (restores umlauts for the ue-canonical value). */
export function displayBundesland(bl?: string | null): string {
  if (!bl) return '';
  return BUNDESLAND_DISPLAY[bl] ?? bl;
}

/**
 * Kürzel der deutschen Bundesländer für die Anzeige am Startort („…, 48149 Münster · NRW"), damit
 * das Bundesland nicht als eigene Zeile unter dem Titel stehen muss. Gewählt sind die Kürzel, die
 * man im Alltag liest (NRW, RLP, NDS), nicht die ISO-Codes. Österreich, die Schweiz und weitere
 * Länder behalten ihren vollen Namen: ihre Kürzel kennt hier kaum jemand.
 */
export const BUNDESLAND_SHORT: Record<string, string> = {
  'Baden-Wuerttemberg': 'BW', 'Bayern': 'BY', 'Berlin': 'BE', 'Brandenburg': 'BB',
  'Bremen': 'HB', 'Hamburg': 'HH', 'Hessen': 'HE', 'Mecklenburg-Vorpommern': 'MV',
  'Niedersachsen': 'NDS', 'Nordrhein-Westfalen': 'NRW', 'Rheinland-Pfalz': 'RLP',
  'Saarland': 'SL', 'Sachsen': 'SN', 'Sachsen-Anhalt': 'ST', 'Schleswig-Holstein': 'SH',
  'Thueringen': 'TH',
};

export function bundeslandShort(bl?: string | null): string {
  if (!bl) return '';
  return BUNDESLAND_SHORT[bl] ?? displayBundesland(bl);
}

/** Diacritic-/case-insensitive lookup key. */
function bundeslandKey(s: string): string {
  return s.trim().toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ');
}

const KEY_TO_CANONICAL: Record<string, string> = {};
BUNDESLAENDER.forEach(b => { KEY_TO_CANONICAL[bundeslandKey(b)] = b; COUNTRY_BY_KEY[bundeslandKey(b)] = 'DE'; });
BUNDESLAENDER_AT.forEach(b => { KEY_TO_CANONICAL[bundeslandKey(b)] = b; COUNTRY_BY_KEY[bundeslandKey(b)] = 'AT'; });
BUNDESLAENDER_CH.forEach(b => { KEY_TO_CANONICAL[bundeslandKey(b)] = b; COUNTRY_BY_KEY[bundeslandKey(b)] = 'CH'; });
FOREIGN_COUNTRIES.forEach(c => { KEY_TO_CANONICAL[bundeslandKey(c.region)] = c.region; COUNTRY_BY_KEY[bundeslandKey(c.region)] = c.code; });

// Non-standard spellings/abbreviations/truncations → canonical key. No Austrian 2-letter codes
// (they'd collide with the German ones, e.g. "st" = Sachsen-Anhalt).
const BUNDESLAND_ALIASES: Record<string, string> = {
  'baden': 'baden-wuerttemberg', 'wuerttemberg': 'baden-wuerttemberg', 'wurttemberg': 'baden-wuerttemberg', 'bw': 'baden-wuerttemberg',
  'by': 'bayern', 'bavaria': 'bayern',
  'be': 'berlin', 'bb': 'brandenburg', 'hb': 'bremen', 'hh': 'hamburg', 'he': 'hessen',
  'mv': 'mecklenburg-vorpommern', 'mecklenburg vorpommern': 'mecklenburg-vorpommern',
  'ni': 'niedersachsen', 'nds': 'niedersachsen',
  'nw': 'nordrhein-westfalen', 'nrw': 'nordrhein-westfalen',
  'rp': 'rheinland-pfalz', 'rlp': 'rheinland-pfalz',
  'sl': 'saarland', 'sn': 'sachsen', 'st': 'sachsen-anhalt',
  'sh': 'schleswig-holstein', 'th': 'thueringen',
  'tyrol': 'tirol', 'carinthia': 'kaernten',
  // Switzerland: multi-word cantons (space/period → hyphenated slug) + French/Italian/English names.
  'st. gallen': 'st-gallen', 'st gallen': 'st-gallen', 'sankt gallen': 'st-gallen',
  'appenzell ausserrhoden': 'appenzell-ausserrhoden', 'appenzell innerrhoden': 'appenzell-innerrhoden',
  'zurich': 'zuerich', 'fribourg': 'freiburg', 'vaud': 'waadt', 'valais': 'wallis',
  'ticino': 'tessin', 'geneve': 'genf', 'genève': 'genf', 'neuchatel': 'neuenburg',
  'neuchâtel': 'neuenburg', 'lucerne': 'luzern', 'berne': 'bern',
  'grisons': 'graubuenden', 'grigioni': 'graubuenden',
  // Bare half-canton names → the more prominent half (Basel = the city; the town Appenzell is in AI).
  'basel': 'basel-stadt', 'appenzell': 'appenzell-innerrhoden',
  // Weitere Länder: Schreibweisen mit Leerzeichen sowie englische/landessprachliche Namen.
  // Keine Zwei-Buchstaben-Kürzel — die kollidieren mit den deutschen Bundesland-Codes.
  'san marino': 'san-marino', 'bosnien und herzegowina': 'bosnien-herzegowina',
  'bosnien': 'bosnien-herzegowina', 'bosnia and herzegovina': 'bosnien-herzegowina',
  'nord-mazedonien': 'nordmazedonien', 'mazedonien': 'nordmazedonien', 'north macedonia': 'nordmazedonien',
  'italy': 'italien', 'italia': 'italien',
  'belgium': 'belgien', 'belgique': 'belgien', 'belgie': 'belgien',
  'netherlands': 'niederlande', 'nederland': 'niederlande', 'holland': 'niederlande',
  'france': 'frankreich', 'spain': 'spanien', 'espana': 'spanien',
  'czech republic': 'tschechien', 'czechia': 'tschechien', 'cesko': 'tschechien',
  'tschechische republik': 'tschechien',
  'poland': 'polen', 'polska': 'polen',
  'denmark': 'daenemark', 'danmark': 'daenemark',
  'sweden': 'schweden', 'sverige': 'schweden',
  'norway': 'norwegen', 'norge': 'norwegen',
  'finland': 'finnland', 'suomi': 'finnland',
  'iceland': 'island', 'ireland': 'irland', 'eire': 'irland',
  'slovenia': 'slowenien', 'slovenija': 'slowenien',
  'slovakia': 'slowakei', 'slovensko': 'slowakei',
  'croatia': 'kroatien', 'hrvatska': 'kroatien',
  'hungary': 'ungarn', 'magyarorszag': 'ungarn',
  'romania': 'rumaenien', 'bulgaria': 'bulgarien', 'serbia': 'serbien', 'srbija': 'serbien',
  'greece': 'griechenland',
  'turkey': 'tuerkei', 'tuerkiye': 'tuerkei', 'türkiye': 'tuerkei',
  'luxembourg': 'luxemburg', 'letzebuerg': 'luxemburg',
  'estonia': 'estland', 'eesti': 'estland', 'latvia': 'lettland', 'latvija': 'lettland',
  'lithuania': 'litauen', 'lietuva': 'litauen',
  'albania': 'albanien', 'shqiperia': 'albanien',
  'moldova': 'moldau', 'cyprus': 'zypern',
  'united kingdom': 'grossbritannien', 'great britain': 'grossbritannien',
  'grossbritannien und nordirland': 'grossbritannien', 'england': 'grossbritannien',
  'schottland': 'grossbritannien', 'scotland': 'grossbritannien', 'wales': 'grossbritannien',
  'nordirland': 'grossbritannien',
  // Länder außerhalb Europas — englische/landessprachliche Namen und Schreibweisen mit Leerzeichen.
  'egypt': 'aegypten', 'misr': 'aegypten',
  'argentina': 'argentinien', 'armenia': 'armenien',
  'australia': 'australien', 'brazil': 'brasilien', 'brasil': 'brasilien',
  'costa rica': 'costa-rica', 'georgia': 'georgien', 'sakartvelo': 'georgien',
  'india': 'indien', 'bharat': 'indien', 'indonesia': 'indonesien',
  'jordan': 'jordanien', 'canada': 'kanada',
  'kazakhstan': 'kasachstan', 'kenya': 'kenia', 'kyrgyzstan': 'kirgisistan',
  'colombia': 'kolumbien', 'morocco': 'marokko', 'maroc': 'marokko', 'al-maghrib': 'marokko',
  'mexico': 'mexiko', 'new zealand': 'neuseeland', 'aotearoa': 'neuseeland',
  'rwanda': 'ruanda',
  'south africa': 'suedafrika', 'republik suedafrika': 'suedafrika',
  'south korea': 'suedkorea', 'korea': 'suedkorea', 'republik korea': 'suedkorea',
  'tanzania': 'tansania', 'tunisia': 'tunesien',
  'united states': 'usa', 'united states of america': 'usa', 'vereinigte staaten': 'usa',
  'vereinigte staaten von amerika': 'usa', 'amerika': 'usa',
  'united arab emirates': 'vereinigte-arabische-emirate', 'uae': 'vereinigte-arabische-emirate',
  'vereinigte arabische emirate': 'vereinigte-arabische-emirate',
  'emirate': 'vereinigte-arabische-emirate',
  'viet nam': 'vietnam',
};

/** Map any input to the canonical ue spelling, or null if it isn't a DE/AT/CH region. */
export function normalizeBundesland(input?: string | null): string | null {
  if (!input) return null;
  const key = bundeslandKey(input);
  if (KEY_TO_CANONICAL[key]) return KEY_TO_CANONICAL[key];
  const alias = BUNDESLAND_ALIASES[key];
  if (alias && KEY_TO_CANONICAL[alias]) return KEY_TO_CANONICAL[alias];
  return null;
}

/** ISO country ('DE'/'AT'/'CH') a region belongs to, or null if it isn't a known region. */
export function countryOfBundesland(input?: string | null): CountryCode | null {
  const canonical = normalizeBundesland(input);
  return canonical ? COUNTRY_BY_KEY[bundeslandKey(canonical)] ?? null : null;
}

export const CATEGORY_LABELS: Record<string, string> = {
  ROAD: 'Rennrad',
  GRAVEL: 'Gravel',
  MTB: 'MTB',
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  // Rennrad / ROAD
  RTF: 'RTF',
  BERGZEITFAHREN: 'Bergzeitfahren',
  BREVET: 'Brevet',
  DUATHLON: 'Duathlon',
  EINZELZEITFAHREN: 'Einzelzeitfahren',
  ETAPPENFAHRT: 'Etappenfahrt',
  ETAPPENRENNEN: 'Etappenrennen',
  HOBBYRENNEN: 'Hobbyrennen',
  JEDERMANN: 'Jedermannrennen',
  MANNSCHAFTSZEITFAHREN: 'Mannschaftszeitfahren',
  RADMARATHON: 'Radmarathon',
  TRIATHLON: 'Triathlon',
  RENNEN_24H: '24 h Rennen',
  RENNEN_12H: '12 h Rennen',
  VINTAGE_TOUR: 'Vintage-Tour',
  VIRTUELLE_RTF: 'Virtuelle RTF',
  VOLKSRADFAHREN: 'Volksradfahren',

  // MTB
  RENNEN_2_3_6H: '2/3/6 h Rennen',
  CROSSCOUNTRY: 'CrossCountry',
  CYCLOCROSS: 'CycloCross',
  CTF: 'CTF',
  DOWNHILL: 'Downhill',
  DUATHLON_CROSS: 'Duathlon (Cross)',
  E_MTB: 'E-MTB',
  ENDURO: 'Enduro',
  ETAPPENTOUR: 'Etappentour',
  HILLCLIMB: 'Hillclimb',
  MTB_MARATHON: 'MTB Marathon',
  MTBO: 'MTBO',
  PUMPTRACKRENNEN: 'Pumptrackrennen',
  TRIATHLON_CROSS: 'Triathlon (Cross)',
  SONSTIGE_MTB: 'Sonstige (MTB)',

  // Gravel — Ride und Tour trennen sich an der Distanz: bis 50 km Ride, darüber Tour.
  GRAVEL_BREVET: 'Gravel Brevet',
  GRAVEL_CAMP: 'Gravel Camp',
  GRAVEL_RACE: 'Gravel Race',
  GRAVEL_RIDE: 'Gravel Ride',
  GRAVEL_TOUR: 'Gravel Tour',
  GRAVEL_SONSTIGES: 'Gravel Sonstiges',

  // Kategorieübergreifend — beide gibt es auf Rennrad, Gravel und MTB gleichermassen.
  // „Ultracycling" statt „Ultra": „Ultra" benennt nur die Distanz und liest sich damit als
  // Oberbegriff, unter den auch Bikepacking fiele — genau die Verwechslung, die die zweite Art
  // auflösen soll. Ultracycling meint das Rennen gegen die Uhr, Bikepacking die selbstversorgte
  // Fahrt mit Gepäck.
  ULTRA: 'Ultracycling',
  BIKEPACKING: 'Bikepacking',

  // Legacy (vor CSV-Migration; bleiben für Altdaten sichtbar)
  MARATHON: 'Marathon',
  ETAPPE: 'Etappenfahrt (alt)',
  TOUR: 'Tour',
  GROUPRIDE: 'Groupride',
  SONSTIGE: 'Sonstige',
};

/**
 * Eventarten je Kategorie (Unterkategorien) — Single Source of Truth für den abhängigen
 * Eventart-Filter und die Formular-Auswahl. Reihenfolge = Anzeige-Reihenfolge (häufigste zuerst).
 * Schlüssel = EventType-Enum-Keys; Labels via EVENT_TYPE_LABELS. Legacy-Typen sind hier bewusst
 * NICHT gelistet (kein Filterziel mehr); Events mit Legacy-Typ erscheinen nur unter „Alle Eventarten".
 */
export const EVENT_TYPES_BY_CATEGORY: Record<string, string[]> = {
  ROAD: [
    'RTF', 'JEDERMANN', 'RADMARATHON', 'BREVET', 'EINZELZEITFAHREN', 'BERGZEITFAHREN',
    'DUATHLON', 'ETAPPENFAHRT', 'ETAPPENRENNEN', 'HOBBYRENNEN', 'MANNSCHAFTSZEITFAHREN',
    'TRIATHLON', 'RENNEN_24H', 'RENNEN_12H', 'VINTAGE_TOUR', 'VIRTUELLE_RTF',
    'VOLKSRADFAHREN', 'ULTRA', 'BIKEPACKING', 'SONSTIGE',
  ],
  MTB: [
    'MTB_MARATHON', 'CROSSCOUNTRY', 'CTF', 'DOWNHILL', 'ENDURO', 'E_MTB', 'CYCLOCROSS',
    'DUATHLON_CROSS', 'TRIATHLON_CROSS', 'RENNEN_2_3_6H', 'RENNEN_12H', 'RENNEN_24H',
    'ETAPPENRENNEN', 'ETAPPENTOUR', 'EINZELZEITFAHREN', 'HILLCLIMB', 'MTBO',
    'PUMPTRACKRENNEN', 'VOLKSRADFAHREN', 'ULTRA', 'BIKEPACKING', 'SONSTIGE_MTB',
  ],
  GRAVEL: [
    'GRAVEL_RACE', 'GRAVEL_TOUR', 'GRAVEL_RIDE', 'GRAVEL_BREVET', 'GRAVEL_CAMP', 'ULTRA',
    'BIKEPACKING', 'GRAVEL_SONSTIGES',
  ],
};

/**
 * Formate, die es in mehreren Disziplinen gibt und nach denen man sucht, ohne zuerst an die
 * Disziplin zu denken (27.10.2026): oben im Eventart-Menü der Startseite, solange keine Disziplin
 * gewählt ist. Ultracycling und Bikepacking stehen unter allen drei Kategorien, die Zeitrennen unter
 * Rennrad und MTB.
 */
export const EVENT_TYPES_CROSS_DISCIPLINE: string[] = ['ULTRA', 'BIKEPACKING', 'RENNEN_24H', 'RENNEN_12H'];

/**
 * Fachliche Ober-/Unterart-Hierarchie — Spiegel von `EVENT_TYPE_CHILDREN` in
 * `backend/.../model/Event.kt` (dort filtert der Server danach). Ein Radmarathon IST eine RTF,
 * MARATHON ist der Legacy-Alias von RADMARATHON. Gespeichert wird pro Event genau eine, möglichst
 * spezifische Art; wer nach der Oberart filtert, bekommt die Unterarten mit.
 *
 * Nur hier, weil die Karte die Trefferzahl selbst rechnet: ohne diese Erweiterung zählte sie bei
 * „RTF" die Radmarathons nicht mit, die die Liste danach zeigt. Neue Paare **in beiden Dateien**
 * ergänzen.
 */
export const EVENT_TYPE_CHILDREN: Record<string, string[]> = {
  RTF: ['RADMARATHON', 'MARATHON'],
};

/**
 * Eventarten, die eine einzelne Strecke tragen darf: die aller Kategorien des Events (Leit- und
 * weitere Kategorien), jede einmal. Ein Volksradfahren auf Rennrad und Gravel kann so eine
 * Gravel-Tour-Strecke haben.
 */
export function eventTypesForCategories(categories: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  categories.forEach(c => (c ? EVENT_TYPES_BY_CATEGORY[c] || [] : []).forEach(t => {
    if (out.indexOf(t) < 0) out.push(t);
  }));
  return out;
}

/**
 * Strecken nach einem Kategorie-Wechsel: die eigene Eventart einer Strecke bleibt nur stehen, wenn
 * sie zu einer der (neuen) Kategorien passt — sonst wird sie geleert. Eine Regel für Leitkategorie,
 * weitere Kategorien und den Review; ohne sie bliebe eine unsichtbare Art im State und würde so
 * gespeichert.
 */
export function distancesForCategories<T extends { eventType?: string | null }>(
  distances: T[],
  categories: Array<string | null | undefined>,
): T[] {
  const allowed = eventTypesForCategories(categories);
  return distances.map(d => d.eventType && !allowed.includes(d.eventType) ? { ...d, eventType: null } : d);
}

/**
 * Eventart einer Strecke, **wenn sie von der des Events abweicht** — sonst `null`. Genau dann
 * nennt die Oberfläche sie an der Strecke; dieselbe Art noch einmal wäre nur Lärm.
 */
export function ownDistanceEventType(
  eventType: string | null | undefined,
  distanceType: string | null | undefined,
): string | null {
  return distanceType && distanceType !== eventType ? distanceType : null;
}

/**
 * Alle Eventarten eines Events: die des Events zuerst, danach die abweichenden Arten seiner
 * Strecken in Streckenreihenfolge, jede einmal. Ein Event mit Gravel-Tour und einer Strecke als
 * Gravel-Race steht damit unter beiden Arten — wie im Eventart-Filter des Servers.
 */
export function eventTypesOf(event: {
  eventType?: string | null;
  distances?: Array<{ eventType?: string | null }> | null;
}): string[] {
  const out: string[] = [];
  if (event.eventType) out.push(event.eventType);
  for (const d of event.distances ?? []) {
    if (d.eventType && out.indexOf(d.eventType) < 0) out.push(d.eventType);
  }
  return out;
}

/** „Gravel-Tour · Gravel-Race" — die Arten aus [eventTypesOf] als Anzeigetext, `null` ohne Art. */
export function eventTypesLabel(event: Parameters<typeof eventTypesOf>[0]): string | null {
  const labels = eventTypesOf(event).map(t => EVENT_TYPE_LABELS[t] ?? t);
  return labels.length > 0 ? labels.join(' · ') : null;
}

/** Anzeigetext der eigenen Eventart einer Strecke, `null` wenn sie der des Events entspricht. */
export function distanceEventTypeLabel(
  eventType: string | null | undefined,
  distanceType: string | null | undefined,
): string | null {
  const own = ownDistanceEventType(eventType, distanceType);
  return own ? (EVENT_TYPE_LABELS[own] ?? own) : null;
}

/**
 * Startzeit an einer Strecke („Start 07:20 Uhr"), `null` ohne eigene Startzeit. Steht auch, wenn
 * sie der des Events gleicht: bei gestaffeltem Start soll jede Kachel ihre Zeit nennen.
 */
export function distanceStartLabel(startTime: string | null | undefined): string | null {
  return startTime ? `Start ${startTime} Uhr` : null;
}

/** Erweitert eine Auswahl um alle Unterarten (transitiv). Nur Oberart → Unterart, nie zurück. */
export function expandEventTypesWithChildren(types: string[]): string[] {
  const result = types.slice();
  const queue = types.slice();
  while (queue.length > 0) {
    const children = EVENT_TYPE_CHILDREN[queue.shift() as string];
    if (!children) continue;
    for (const child of children) {
      if (result.indexOf(child) < 0) { result.push(child); queue.push(child); }
    }
  }
  return result;
}

/**
 * Eventart-Keys alphabetisch nach ihrem deutschen Label sortiert — für die Anzeige in Dropdowns.
 * Die Reihenfolge der Quell-Konstanten (häufigste zuerst) bleibt für andere Konsumenten erhalten.
 */
export const sortEventTypesByLabel = (keys: string[]): string[] =>
  [...keys].sort((a, b) => (EVENT_TYPE_LABELS[a] ?? a).localeCompare(EVENT_TYPE_LABELS[b] ?? b, 'de'));

/** Alle [key, label]-Einträge aus EVENT_TYPE_LABELS, alphabetisch nach Label — für flache Dropdowns. */
export const EVENT_TYPE_LABEL_ENTRIES_SORTED: Array<[string, string]> =
  Object.entries(EVENT_TYPE_LABELS).sort((a, b) => a[1].localeCompare(b[1], 'de'));

// --- Wetter (Bright Sky / DWD) ---

/** Bright-Sky-Icon-Key → Emoji für die Wetteranzeige. */
const WEATHER_EMOJI: Record<string, string> = {
  'clear-day': '☀️',
  'clear-night': '🌙',
  'partly-cloudy-day': '⛅',
  'partly-cloudy-night': '☁️',
  cloudy: '☁️',
  fog: '🌫️',
  wind: '💨',
  rain: '🌧️',
  sleet: '🌨️',
  snow: '❄️',
  hail: '🌨️',
  thunderstorm: '⛈️',
};

export function weatherEmoji(icon?: string | null): string {
  return (icon && WEATHER_EMOJI[icon]) || '☁️';
}

/** Bright-Sky-Condition → deutsches Label. */
const WEATHER_CONDITION_LABELS: Record<string, string> = {
  dry: 'Trocken',
  fog: 'Nebel',
  rain: 'Regen',
  sleet: 'Schneeregen',
  snow: 'Schnee',
  hail: 'Hagel',
  thunderstorm: 'Gewitter',
};

export function weatherLabel(condition?: string | null): string {
  return (condition && WEATHER_CONDITION_LABELS[condition]) || '—';
}

/** News category accent colors (keyed by NewsCategory; SZENE = brand purple). */
export const NEWS_CATEGORY_COLORS: Record<string, string> = {
  ...CATEGORY_COLORS,
  SZENE: '#922C57',
};

export const NEWS_CATEGORY_LABELS: Record<string, string> = {
  ...CATEGORY_LABELS,
  SZENE: 'Allgemein',
};

/** Link-Typen (EventLink.type) → deutsches Label für Formulare und Event-Seite. */
export const LINK_TYPE_LABELS: Record<string, string> = {
  REGISTRATION: 'Anmeldung',
  ANNOUNCEMENT: 'Ausschreibung',
  WEBSITE: 'Website',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  SOURCE: 'Quelle',
  BREITENSPORT: 'Breitensport-Kalender',
  OTHER: 'Sonstiger Link',
};

/**
 * Reihenfolge im Typ-Dropdown und beim Anzeigen: das, was ein Besucher am ehesten
 * braucht, zuerst. BREITENSPORT/SOURCE sind Pflege-Typen und stehen hinten.
 */
export const LINK_TYPE_ORDER: string[] = [
  'REGISTRATION',
  'ANNOUNCEMENT',
  'WEBSITE',
  'INSTAGRAM',
  'FACEBOOK',
  'SOURCE',
  'BREITENSPORT',
  'OTHER',
];

export const LINK_VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Öffentlich',
  INTERNAL: 'Nur intern',
};

/**
 * Hinweis an jedem Weg, auf dem über einen Einmal-Code ein Konto entstehen kann (Anmelden,
 * Registrieren, Glocke, Mail-Anmeldung, Veranstalter-Zugang). Der Code legt ein unbekanntes Konto
 * stillschweigend an, deshalb muss das dort stehen, wo die Adresse eingegeben wird, samt Link auf
 * die Datenschutzerklärung. `ACCOUNT_CREATED_NOTE` entfällt, wo die Seite selbst schon
 * „Konto erstellen" heißt. Gezeichnet als `<Text> <Link>` + Punkt.
 */
export const ACCOUNT_CREATED_NOTE = 'Neu hier? Dann legen wir dir dabei ein kostenloses Konto an.';
export const PRIVACY_NOTE_LEAD = 'Es gilt unsere';
export const PRIVACY_NOTE_LINK = 'Datenschutzerklärung';
export const PRIVACY_PATH = '/datenschutz';
export const PRIVACY_URL = 'https://radsport-events.de/datenschutz';
